import {
  Alert,
  Box,
  Flex,
  Center,
  Checkbox,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  Button,
  Text,
  TextInput,
  HStack,
  VStack,
  Switch,
  Tooltip,
} from '@televet/kibble-ui';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import isEqual from 'lodash-es/isEqual';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import useClinicUser from 'shared/hooks/useClinicUser';
import debounce from 'lodash-es/debounce';
import {
  PermissionType,
  QueryMode,
  SortOrder,
  useGetLoyaltyProgramPointGrantingServicesQuery,
  useUpdateOneLoyaltyInvoicePointGranterMutation,
  ServiceFromIntegrationIdFragment,
  useGetServicesByIntegrationIdQuery,
  useGetServicesByIntegrationIdLazyQuery,
  useGetServicesIdsByIntegrationIdLazyQuery,
} from 'shared/types/graphql';
import Dropdown, { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import { scrollIntoView } from 'shared/utils';
import { Pagination } from 'shared/components/Pagination';
import { UnsavedChangesProps } from 'pages/LoyaltyProgram';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTracking } from 'shared/providers/TrackingProvider';

enum EligableItemsMenuOption {
  SelectCurrentPage = 'SelectCurrentPage',
  SelectAllPages = 'SelectAllPages',
  ClearAll = 'ClearAll',
}

const eligibleItemsOptions = [
  {
    text: 'Select This Page',
    value: EligableItemsMenuOption.SelectCurrentPage,
  },
  {
    text: 'Select All Pages',
    value: EligableItemsMenuOption.SelectAllPages,
  },
  {
    text: 'Clear All Selected',
    value: EligableItemsMenuOption.ClearAll,
  },
];

const sortOptions = [
  { name: SortOrder.Asc },
  { name: SortOrder.Desc },
  { code: SortOrder.Asc },
  { code: SortOrder.Desc },
];

const EligibleItems = forwardRef(({ setHasUnsavedChange, viewRef }: UnsavedChangesProps, ref): JSX.Element => {
  const { isOpen, onToggle } = useDisclosure();
  const { currentClinicId, userHasPermission } = useClinicUser();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(10);
  const [selectedServicesIds, setSelectedServicesIds] = useState<string[]>([]);
  const [allServicesSelected, setAllServicesSelected] = useState<boolean>(false);
  const [initialServices, setInitialServices] = useState<ServiceFromIntegrationIdFragment[]>([]);
  const [initialServicesIds, setInitialServicesIds] = useState<string[]>([]);
  const [deselectedServiceIds, setDeselectedServiceIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<
    typeof sortOptions[0] | typeof sortOptions[1] | typeof sortOptions[2] | typeof sortOptions[3]
  >(sortOptions[0]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const { toastMessagePop } = useCustomToast();
  const [bigQueryMode, setBigQueryMode] = useState(false);
  const [allServicesIds, setAllServicesIds] = useState<string[]>([]);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const { trackEvent } = useTracking();

  const canEdit = userHasPermission(PermissionType.WriteClinic);
  const { allActiveIntegrations, primaryIntegrationName } = useIntegrationsProvider();

  // return an array of all active integration ids
  const integrationIds = useMemo(() => {
    return allActiveIntegrations?.map((integration) => integration.id) || [];
  }, [allActiveIntegrations]);

  const [getAllServicesIds, { loading: loadingAllServicesIds }] = useGetServicesIdsByIntegrationIdLazyQuery({
    variables: {
      where: {
        integrationId: { in: integrationIds },
        isDeleted: { equals: false },
      },
    },
    onCompleted: (data) => {
      if (data.servicesIds) {
        const ids = data.servicesIds.map((service) => service.id);
        setSelectedServicesIds(ids);
        setAllServicesIds(ids);
      }
    },
  });

  const { data: loyaltyProgramPointGranterInfo } = useGetLoyaltyProgramPointGrantingServicesQuery({
    variables: {
      where: {
        participatingClinics: {
          some: {
            id: {
              equals: currentClinicId,
            },
          },
        },
      },
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    onCompleted: (data) => {
      if (data.findFirstLoyaltyProgram) {
        const { findFirstLoyaltyProgram } = data;
        const { pointGranters } = findFirstLoyaltyProgram;
        const enabledPointGranters = pointGranters.filter((pointGranter) => pointGranter.enabled);
        const allowAllServices = enabledPointGranters.some((pointGranter) => pointGranter.allowAllServices);
        if (allowAllServices) {
          setAllServicesSelected(true);
        }
        const { grantingServices } = enabledPointGranters[0];
        const services: ServiceFromIntegrationIdFragment[] = grantingServices.map(
          (grantingService) => grantingService.service,
        );
        if (allowAllServices === false) {
          setSelectedServicesIds(services.map((service) => service.id));
          setInitialServicesIds(services.map((service) => service.id));
          setInitialServices(services);
        }
      }
    },
  });

  const pointGranterId =
    loyaltyProgramPointGranterInfo?.findFirstLoyaltyProgram?.pointGranters.find((pointGranter) => pointGranter.enabled)
      ?.id || '';

  const allowAllServices = useMemo(() => {
    return loyaltyProgramPointGranterInfo?.findFirstLoyaltyProgram?.pointGranters.find(
      (pointGranter) => pointGranter.enabled,
    )?.allowAllServices;
  }, [loyaltyProgramPointGranterInfo?.findFirstLoyaltyProgram?.pointGranters]);

  const {
    data: serviceData,
    previousData: previousServiceData,
    loading: isServicesLoading,
    refetch: refetchServices,
  } = useGetServicesByIntegrationIdQuery({
    skip: !integrationIds.length,
    variables: {
      where: {
        integrationId: { in: integrationIds },
        OR: [
          {
            code: { contains: debouncedSearchText, mode: QueryMode.Insensitive },
          },
          {
            name: { contains: debouncedSearchText, mode: QueryMode.Insensitive },
          },
        ],
        isDeleted: { equals: false },
      },
      orderBy: [sortBy],
      take: currentPageSize,
      skip: currentPageSize * (currentPage - 1),
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    notifyOnNetworkStatusChange: true,
    partialRefetch: true,
  });

  useEffect(() => {
    if (searchText !== '' && !!serviceData?.findManyServiceCount && serviceData?.findManyServiceCount > 10) {
      setBigQueryMode(true);
    } else {
      setBigQueryMode(false);
    }
  }, [searchText, serviceData?.findManyServiceCount]);

  // use a Lazy Query to get all services needed to be returned in a search with many results
  const [getSearchServices, { previousData: previousLazyServiceData }] = useGetServicesByIntegrationIdLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    onCompleted: (data) => {
      if (data.findManyService) {
        const { findManyService } = data;
        setInitialServices({ ...findManyService });
        setSelectedServicesIds(findManyService.map((service: ServiceFromIntegrationIdFragment) => service.id));
      }
    },
  });

  // input for the createMany input for updateGranter needs to have the following structure [{serviceId: '123'}, {serviceId: '456'}] for each service in the selectedServiceIds array
  const createServiceIds = useMemo(() => {
    // if all services are selected and there is no search text we only need to update allowAllServices to true
    if (allServicesSelected === true && searchText === '') {
      return [];
    }
    //selected service ids with initial services ids filtered out since they have already been created, if intial services is empty then we just return selected service ids
    if (initialServices.length > 0) {
      const newServices = selectedServicesIds.filter(
        (id) => !initialServices?.map((service) => service?.id).includes(id),
      );
      return newServices?.map((id) => ({ serviceId: id }));
    } else if (selectedServicesIds.length > 0) {
      return selectedServicesIds?.map((id) => ({ serviceId: id }));
    }
    return [];
  }, [allServicesSelected, initialServices, searchText, selectedServicesIds]);

  // input for the deleteMany input for updateGranter needs to have the following structure [{serviceId: { equals: '123'}}, {serviceId: {equals: '456'}}] for each service in the selectedServiceIds array
  const deleteServiceIds = useMemo(() => {
    //if all services selected is true return all initial services to remove them from the granting services
    if (allServicesSelected && initialServices.length > 0 && searchText === '') {
      return initialServices.map((service) => ({ serviceId: { equals: service.id } }));
    }
    // otherwise we need to remove all services added to the deselectServices array if there are any
    if (deselectedServiceIds.length > 0) {
      const serviceIds = deselectedServiceIds.map((serviceId) => {
        return { serviceId: { equals: serviceId } };
      });
      return serviceIds;
    }
    return [];
  }, [allServicesSelected, deselectedServiceIds, initialServices, searchText]);

  const [updateGranter] = useUpdateOneLoyaltyInvoicePointGranterMutation({
    variables: {
      where: {
        id: pointGranterId,
      },
      data: {
        allowAllServices: allServicesSelected,
        grantingServices: {
          createMany: {
            data: createServiceIds,
          },
          deleteMany: deleteServiceIds,
        },
      },
    },
    onCompleted: (data) => {
      if (data.updateOneLoyaltyInvoicePointGranter) {
        setHasUnsavedChange(false);
        setInitialServicesIds(selectedServicesIds);
        setDeselectedServiceIds([]);
        refetchServices();
      }
    },
    refetchQueries: ['getLoyaltyProgramPointGrantingServices'],
  });

  useImperativeHandle(ref, () => ({
    onLeavingCurrentPage(): void {
      setInitialServices([]);
      setInitialServicesIds([]);
      setSelectedServicesIds([]);
      setDeselectedServiceIds([]);
      setAllServicesSelected(false);
    },
    onClickedSaveButton(): void {
      if (bigQueryMode === true) {
        getSearchServices({
          variables: {
            where: {
              integrationId: { in: integrationIds },
              OR: [
                {
                  code: { contains: debouncedSearchText, mode: QueryMode.Insensitive },
                },
                {
                  name: { contains: debouncedSearchText, mode: QueryMode.Insensitive },
                },
              ],
              isDeleted: { equals: false },
            },
          },
        });
      }
      updateGranter()
        .then(() => {
          toastMessagePop();
          refetchServices();
        })
        .catch(() => {
          toastMessagePop(
            'Whoops! There was a problem saving your changes, please refresh the page and try again.',
            STATUSES.error,
          );
        });
    },
    onClickedCancelButton(): void {
      setSelectedServicesIds(initialServicesIds);
      setAllServicesSelected(!!allowAllServices);
    },
  }));

  useEffect(() => {
    if (allowAllServices === true && allServicesSelected === true && searchText === '') {
      // when allowAllServices is true and allServicesSelected is true and there are no unsaved changes no matter what page the user is on
      setHasUnsavedChange(false);
      return;
    }

    if (selectedServicesIds && initialServicesIds) {
      setHasUnsavedChange(
        isEqual(selectedServicesIds, initialServicesIds) && allowAllServices === allServicesSelected ? false : true,
      );
      return;
    }
  }, [setHasUnsavedChange, allServicesSelected, allowAllServices, searchText, selectedServicesIds, initialServicesIds]);

  const handleSearchInputKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      setSearchText('');
    }
  }, []);

  // eslint-disable-next-line
  const handleSearchTextChange = useCallback<((searchText: string) => void) & { flush: () => void | undefined }>(
    debounce(
      (searchText): void => {
        const text = searchText.trim();
        setDebouncedSearchText(text);
        setCurrentPage(1);
      },
      340,
      { leading: false, trailing: true },
    ),
    [currentClinicId],
  );

  useEffect(() => {
    handleSearchTextChange(searchText);
  }, [searchText, handleSearchTextChange]);

  const handleCheckboxClick = (data: ServiceFromIntegrationIdFragment): void => {
    if (!isOpen) onToggle();

    const { id } = data;

    const isServiceSelected = selectedServicesIds.includes(id);

    if (isServiceSelected) {
      //filter out the service that was deselected from selected services
      const updatedIds = [...selectedServicesIds.filter((serviceId: string) => serviceId !== id)];
      setSelectedServicesIds(updatedIds);

      // add the deselected service to the deselectedServiceIds array
      setDeselectedServiceIds([...deselectedServiceIds, id]);
      if (!updatedIds.length && isOpen) onToggle();
    } else {
      // add the selected service to the selectedServices array
      setSelectedServicesIds([...selectedServicesIds, id]);

      // filter out the deselected service from the deselectedServiceIds array
      const updatedDeselectedServiceIds = [
        ...deselectedServiceIds.filter((deselectedServiceId: string) => deselectedServiceId !== id),
      ];
      setDeselectedServiceIds(updatedDeselectedServiceIds);
    }
  };

  const selectCurrentPageServices = (): void => {
    const currentPageIds = serviceData?.findManyService?.map((service) => service.id) || [];
    // add the selected services on the page to the selectedServicesIds array
    const ids = Array.from(new Set([...selectedServicesIds, ...currentPageIds]));
    setSelectedServicesIds(ids);
    // remove the deselected services on the page from the deselectedServiceIds array
    const deselectedIds = deselectedServiceIds.filter((id) => !currentPageIds.includes(id));
    setDeselectedServiceIds(deselectedIds);
    if (!isOpen) onToggle();
  };

  const selectAllServices = (): void => {
    if (!allServicesIds.length) {
      getAllServicesIds();
    } else {
      setSelectedServicesIds(allServicesIds);
    }
    if (!isOpen) onToggle();
  };

  const deselectAllServices = (): void => {
    setSelectedServicesIds([]);
    setDeselectedServiceIds(initialServices ? initialServices?.map((service) => service.id) : []);
    if (isOpen) onToggle();
  };

  const handleMultiSelectCheckboxClick = (): void => {
    trackEvent(LoyaltyEvents.EligibleItemsCheckboxClick, { type: 'global' });
    if (areNoServicesSelected) {
      selectCurrentPageServices();
    } else if (areAllServicesSelected) {
      deselectAllServices();
    } else {
      selectAllServices();
    }
  };

  const handleServiceSettingsMenuOptionClick = (
    _event: React.MouseEvent<Element, globalThis.MouseEvent>,
    option: IOption<EligableItemsMenuOption>,
  ): void => {
    const { value } = option;

    if (value === EligableItemsMenuOption.SelectAllPages) {
      selectAllServices();
    } else if (value === EligableItemsMenuOption.SelectCurrentPage) {
      selectCurrentPageServices();
    } else if (value === EligableItemsMenuOption.ClearAll) {
      deselectAllServices();
    }
  };

  const handlePageChange = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  const handlePageSize = useCallback(
    (pageSize: number) => {
      handlePageChange(1);
      setCurrentPageSize(pageSize);
    },
    [handlePageChange, setCurrentPageSize],
  );

  const noResults = useMemo(() => {
    return debouncedSearchText && !isServicesLoading && !serviceData?.findManyServiceCount;
  }, [debouncedSearchText, isServicesLoading, serviceData]);

  const areAllServicesSelected = useMemo(() => {
    return selectedServicesIds.length === serviceData?.findManyServiceCount;
  }, [selectedServicesIds.length, serviceData?.findManyServiceCount]);

  const areSomeServicesSelected = useMemo(() => {
    return selectedServicesIds.length > 0 && !areAllServicesSelected;
  }, [areAllServicesSelected, selectedServicesIds.length]);

  const areNoServicesSelected = useMemo(() => {
    return selectedServicesIds.length === 0;
  }, [selectedServicesIds]);

  const handleSortByCode = useCallback(() => {
    if (sortBy === sortOptions[0] || sortBy === sortOptions[1]) {
      setSortBy(sortOptions[2]);
    } else if (sortBy === sortOptions[2]) {
      setSortBy(sortOptions[3]);
    } else {
      setSortBy(sortOptions[2]);
    }
    refetchServices();
  }, [refetchServices, sortBy]);

  const handleSortByName = useCallback(() => {
    if (sortBy === sortOptions[2] || sortBy === sortOptions[3]) {
      setSortBy(sortOptions[0]);
    } else if (sortBy === sortOptions[0]) {
      setSortBy(sortOptions[1]);
    } else {
      setSortBy(sortOptions[0]);
    }
    refetchServices();
  }, [refetchServices, sortBy]);

  useEffect(() => {
    // Scrolls top of table into view if table is not in view after paging.
    if (!isServicesLoading && !noResults) {
      if (viewRef?.current && tableRef.current) scrollIntoView(tableRef.current, viewRef.current, 500);
    }
  }, [isServicesLoading, noResults, viewRef]);

  const handleSwitchChange = (): void => {
    if (allServicesSelected) {
      if (!allServicesIds.length) {
        getAllServicesIds();
      } else {
        setSelectedServicesIds(allServicesIds);
      }
    }

    setAllServicesSelected((current) => !current);
  };

  return isServicesLoading || loadingAllServicesIds ? (
    <LoadingIndicator />
  ) : (
    <>
      <Flex justifyContent="space-between" mb={5}>
        <TextInput
          name="search"
          placeholder="Search code or name..."
          value={searchText}
          onChange={(e): void => setSearchText(e.target.value)}
          onKeyDown={handleSearchInputKeyDown}
          leftElement={<Icon name="magnifyingGlass" size="md" />}
          isClearable
          onClear={(): void => setSearchText('')}
          formControlStyle={{ maxW: '320px' }}
        />
        <Flex alignItems="center" justifyContent="flex-end">
          <Flex alignItems="center" mr={2}>
            <Text
              mr={1}
              {...(!allServicesSelected && { fontWeight: 'semibold' })}
              variant={allServicesSelected ? 'subtle' : 'default'}
            >
              Manual
            </Text>
            <Tooltip
              label={`If "Enable All" is turned off, new services synced from ${
                primaryIntegrationName ? primaryIntegrationName : 'your PIMS'
              } will not be enabled by default.`}
              titleCaseLabel={false}
            >
              <Flex>
                <Icon name="infoCircle" size="md" variant="subtle" />
              </Flex>
            </Tooltip>
          </Flex>
          <Switch
            isFieldInline
            formControlStyle={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', w: 'auto' }}
            isChecked={allServicesSelected}
            onChange={handleSwitchChange}
            isDisabled={!canEdit}
          />
          <Text
            ml={3}
            {...(allServicesSelected && { fontWeight: 'semibold' })}
            variant={allServicesSelected ? 'default' : 'subtle'}
          >
            Enable All
          </Text>
        </Flex>
      </Flex>
      <Alert
        title={
          allServicesSelected ? 'All services are enabled' : `${selectedServicesIds?.length || '0'} services selected`
        }
        hideCloseButton
        hideIcon
        variant="info"
      />
      <TableContainer ref={tableRef}>
        <Table variant="unstyled" style={{ fontVariantNumeric: 'normal' }}>
          <Thead>
            <Tr borderBottomWidth="thin" px={2} pb={3}>
              <Th>
                {allServicesSelected && <Checkbox isDisabled isChecked={allServicesSelected} px={1} />}
                {canEdit && !allServicesSelected && (
                  <Box w="48px">
                    <Dropdown
                      options={eligibleItemsOptions}
                      onSelect={handleServiceSettingsMenuOptionClick}
                      placement={PopoverPlacement.BottomStart}
                    >
                      <Flex
                        align="center"
                        borderRadius="md"
                        _hover={{ bgColor: 'background.alpha.hover' }}
                        px={1}
                        py={1.5}
                        cursor="pointer"
                      >
                        <Checkbox
                          isChecked={areAllServicesSelected}
                          isIndeterminate={areSomeServicesSelected}
                          onChange={handleMultiSelectCheckboxClick}
                          mr={1}
                        />
                        <HStack>
                          <Icon name="chevronDown" size="sm" />
                        </HStack>
                      </Flex>
                    </Dropdown>
                  </Box>
                )}
              </Th>
              <Th>
                <HStack>
                  <Text size="sm">Code</Text>
                  <Button
                    _focus={{ outline: 'none' }}
                    iconName="sort"
                    variant="ghostNeutral"
                    iconPosition="right"
                    aria-label="Add or remove filter options"
                    size="xs"
                    onClick={handleSortByCode}
                  />
                </HStack>
              </Th>
              <Th>
                <HStack>
                  <Text size="sm">Name</Text>
                  <Button
                    _focus={{ outline: 'none' }}
                    iconName="sort"
                    variant="ghostNeutral"
                    iconPosition="right"
                    aria-label="Add or remove filter options"
                    size="xs"
                    onClick={handleSortByName}
                  />
                </HStack>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {serviceData?.findManyService ? (
              serviceData?.findManyService?.map((service, index) => (
                <Tr key={service.id} backgroundColor={index % 2 ? 'background.lessSubtle' : 'transparent'}>
                  <Td display="inline-flex">
                    {canEdit && (
                      <Checkbox
                        px={1}
                        borderRadius="md"
                        onChange={(): void => {
                          trackEvent(LoyaltyEvents.EligibleItemsCheckboxClick, { type: 'individual' });
                          handleCheckboxClick(service);
                        }}
                        isChecked={allServicesSelected ? true : selectedServicesIds.includes(service.id)}
                        isDisabled={allServicesSelected}
                        display="inline-flex"
                      />
                    )}
                  </Td>
                  <Td>
                    <Text>{service.code}</Text>
                  </Td>
                  <Td>
                    <Text>{service.name}</Text>
                  </Td>
                </Tr>
              ))
            ) : (
              <Center my={7}>
                <Text size="sm">No Services Found.</Text>
              </Center>
            )}
            {noResults && (
              <Center my={7}>
                <Text size="sm">No results found for &quot;{debouncedSearchText}&quot;</Text>
              </Center>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <VStack
        flex="1 1 auto"
        spacing={0}
        w="100%"
        h="100%"
        justifyContent="flex-start"
        alignItems="stretch"
        marginInlineStart="0px !important"
      ></VStack>
      <>
        {!noResults && (
          <Box my={5}>
            <Pagination
              recordCount={
                serviceData?.findManyServiceCount ||
                previousServiceData?.findManyServiceCount ||
                previousLazyServiceData?.findManyServiceCount ||
                0
              }
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onPageSize={handlePageSize}
              pageSize={currentPageSize}
              isLoading={isServicesLoading}
            />
          </Box>
        )}
      </>
    </>
  );
});

EligibleItems.displayName = 'Eligible Items';

export default EligibleItems;
